import React, { useState, useEffect } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { logger } from '../utils/logger';

// ─── Brand tokens (inline pra preservar fidelidade visual) ───
const IAT = {
  yellow:     '#FFC107',
  yellowDeep: '#F59E0B',
  graphite:   '#1A1A20',
  charcoal:   '#232329',
  iron:       '#5A5A65',
  pewter:     '#C4C4CC',
  paper:      '#FAFAFB',
  ink:        '#0E0E12',
  borderDark: '#2D2D35',
};
const FONT_SANS    = "'Inter', -apple-system, system-ui, sans-serif";
const FONT_DISPLAY = "'Inter Tight', 'Inter', system-ui, sans-serif";

const ModernUpdateToast = ({ hasUpdate, serverVersion, onReload }) => {
  const [forceShow, setForceShow] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Permitir forçar via window em dev — útil pra preview do visual sem deploy
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.__SHOW_UPDATE_NOTIFICATION__ = () => {
        logger.log('[TEST] Forçando notificação a aparecer...');
        setForceShow(true);
      };
    }
  }, []);

  useEffect(() => {
    logger.log('[ModernUpdateToast] hasUpdate mudou para:', hasUpdate);
  }, [hasUpdate]);

  useEffect(() => {
    if ((hasUpdate || forceShow) && !dismissed) {
      logger.log('[ModernUpdateToast] ✅ Atualização detectada, mostrando notificação...');
      const t = setTimeout(() => {
        setIsVisible(true);
        logger.log('[ModernUpdateToast] ✅ Notificação visível!');
      }, 100);
      return () => clearTimeout(t);
    }
    setIsVisible(false);
  }, [hasUpdate, forceShow, dismissed]);

  if ((!hasUpdate && !forceShow) || dismissed) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        opacity: isVisible ? 1 : 0,
        transition: 'transform 0.45s ease, opacity 0.45s ease',
        pointerEvents: isVisible ? 'auto' : 'none',
        maxWidth: 360,
        width: 'calc(100vw - 48px)',
      }}
      role="status"
      aria-live="polite"
    >
      <div
        style={{
          background: IAT.graphite,
          color: IAT.paper,
          padding: '18px 18px 16px',
          borderRadius: 16,
          border: `1px solid ${IAT.borderDark}`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,193,7,0.08)',
          fontFamily: FONT_SANS,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Glow amarelo decorativo */}
        <div
          style={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 140,
            height: 140,
            background: `radial-gradient(circle, rgba(255,193,7,0.22), transparent 70%)`,
            filter: 'blur(20px)',
            pointerEvents: 'none',
          }}
        />

        {/* Botão fechar (X) */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dispensar notificação"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: IAT.iron,
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
          }}
          onMouseOver={(e) => { e.currentTarget.style.color = IAT.paper; }}
          onMouseOut={(e)  => { e.currentTarget.style.color = IAT.iron;  }}
        >
          <X size={14} />
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          <div
            style={{
              width: 28, height: 28, borderRadius: 8,
              background: 'rgba(255,193,7,0.14)',
              border: '1px solid rgba(255,193,7,0.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: IAT.yellow,
              flexShrink: 0,
            }}
          >
            <Lightbulb size={14} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: 15,
                color: IAT.paper,
                letterSpacing: '-0.01em',
              }}
            >
              Nova versão disponível
            </div>
            <div style={{ fontSize: 12, color: IAT.pewter, marginTop: 2 }}>
              {serverVersion ? `Versão ${serverVersion}` : 'Atualização pronta para instalar'}
            </div>
          </div>
        </div>

        {/* Botão principal */}
        <button
          type="button"
          onClick={() => {
            if (onReload && typeof onReload === 'function') onReload();
            else window.location.reload();
          }}
          style={{
            marginTop: 14,
            width: '100%',
            background: IAT.yellow,
            color: IAT.ink,
            border: 'none',
            borderRadius: 10,
            padding: '11px 16px',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: FONT_SANS,
            letterSpacing: '-0.005em',
            cursor: 'pointer',
            boxShadow: '0 8px 22px rgba(255,193,7,0.32)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
            position: 'relative',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = IAT.yellowDeep;
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = IAT.yellow;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Atualizar agora
        </button>
      </div>
    </div>
  );
};

export default ModernUpdateToast;
